"use client"

import React from 'react'
import { X, Filter } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { useCurrentSearchParams } from '@/shared/hooks/use-current-search-params'
import { GetHomeOwnerQueryParamsType } from '../schema/home/query-params.schema'
import { buildUrl } from '@/shared/lib/utils'
import { useRouter, usePathname, useSearchParams } from 'next/navigation'
import { homeOwnerAgeOptions, yesNoBooleanOptions, yesNoUnknownOptions, yesNoRejectedOptions } from '@/shared/constants/options'
import { residenceTypeOptions, structureTypeOptions, overallStatusOptions, goodwillStatusOptions, workScopeStatusOptions, kindOfRoofWorkOptions } from '../constants/options'
import { normalizeOverallStatusList } from '../lib/overall-status.utils'
import { useWorkScopes } from '@/features/work-orders/hooks/use-work-scopes'
import { WORK_SCOPE_GOODWILL_OPTIONS } from '../lib/workscope-goodwill-filter'
import { getClaimedByDisplayName } from '../lib/claimed-by-display'

// Helper function to get display label for filter values
const getFilterDisplayLabel = (
    key: string,
    value: string | number | string[] | null | undefined,
    workScopeOptions?: { label: string; value: string }[]
) => {
    // Handle null/undefined values
    if (value === null || value === undefined) {
        return ''
    }

    // Normalize comma-separated URL params to array for multiselect keys
    const multiselectKeys = ['overallStatus', 'workscope', 'claimedBy', 'workscopeStatus', 'workscopeGoodwill', 'workscopeGoodwillStatus', 'reliefStatus', 'rebuildingStatus', 'goodwillStatus', 'roofing', 'kindOfRoofWork', 'residenceType', 'structureType', 'assessmentCompleted', 'city', 'county']
    if (typeof value === 'string' && multiselectKeys.includes(key)) {
        value = value.split(',').map((s: string) => s.trim()).filter(Boolean)
        if (key === 'overallStatus') {
            value = normalizeOverallStatusList(value)
        }
    }

    // Handle array values (for multiselect filters)
    if (Array.isArray(value)) {
        if (key === 'overallStatus') {
            value = normalizeOverallStatusList(value)
        }
        const optionMaps: Record<string, readonly { label: string; value: string }[]> = {
            overallStatus: overallStatusOptions,
            reliefStatus: goodwillStatusOptions,
            rebuildingStatus: goodwillStatusOptions,
            goodwillStatus: goodwillStatusOptions,
            workscopeGoodwillStatus: workScopeStatusOptions,
            roofing: workScopeStatusOptions,
            workscopeStatus: workScopeStatusOptions,
            kindOfRoofWork: kindOfRoofWorkOptions,
        }
        if (key === 'workscope' && workScopeOptions?.length) {
            const labels = value.map(v => {
                const option = workScopeOptions.find(opt => opt.value === v)
                return option?.label || v
            })
            return labels.join(', ')
        }
        if (key === 'workscopeGoodwill' && WORK_SCOPE_GOODWILL_OPTIONS?.length) {
            const labels = value.map(v => {
                const option = WORK_SCOPE_GOODWILL_OPTIONS.find(opt => opt.value === v)
                return option?.label || v
            })
            return labels.join(', ')
        }
        const options = optionMaps[key]
        if (options) {
            const labels = value.map(v => {
                const option = options.find(opt => opt.value === v)
                return option?.label || v
            })
            return labels.join(', ')
        }
        if (key === 'claimedBy') return value.map(getClaimedByDisplayName).join(', ')
        // city / county: value is array of names, join for display
        if (key === 'city' || key === 'county') return value.join(', ')
        return value.join(', ')
    }

    // Convert number to string for processing
    const stringValue = String(value)

    // Format YYYY-MM-DD for Updated/Added date filters
    if ((key === 'updatedFrom' || key === 'updatedTo' || key === 'addedFrom' || key === 'addedTo') && /^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
        return new Date(String(value) + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    }

    // Active/Inactive filter
    if (key === 'activeStatus' && (value === 'active' || value === 'inactive')) {
        return value === 'active' ? 'Active only' : 'Inactive only'
    }

    // Special handling for search parameter
    if (key === 'search') {
        // Check if the search value is a home ID (numeric and > 1000)
        const isNumericSearch = /^\d+$/.test(stringValue)
        const searchAsNumber = parseInt(stringValue)
        
        if (isNumericSearch && searchAsNumber > 1000) {
            return `home id = ${stringValue}`
        }
        
        // For text searches, return the search term as-is
        return stringValue
    }

    const optionMaps = {
        medicalNeeds: yesNoBooleanOptions,
        homeownerAge: homeOwnerAgeOptions,
        isSingleParent: yesNoBooleanOptions,
        isFirstResponder: yesNoBooleanOptions,
        isVeteran: yesNoBooleanOptions,
        hasFloodInsurance: yesNoUnknownOptions,
        hasHomeInsurance: yesNoUnknownOptions,
        femaStatus: yesNoRejectedOptions,
        sbaStatus: yesNoRejectedOptions,
        residenceType: residenceTypeOptions,
        structureType: structureTypeOptions,
    }

    const options = optionMaps[key as keyof typeof optionMaps]
    if (options) {
        const option = options.find(opt => opt.value === stringValue)
        return option?.label || stringValue
    }

    // For text fields, capitalize the first letter
    if ((key === 'city' || key === 'county') && typeof value === 'string') {
        return value.split(',').map((s: string) => s.trim()).filter(Boolean).join(', ')
    }
    return stringValue.charAt(0).toUpperCase() + stringValue.slice(1)
}

// Helper function to get filter display name
const getFilterDisplayName = (key: string) => {
    const displayNames: Record<string, string> = {
        overallStatus: 'Overall Home Status',
        reliefStatus: 'Relief Status',
        rebuildingStatus: 'Rebuilding Status',
        workscope: 'Workscope',
        claimedBy: 'Claimed By',
        workscopeStatus: 'Status of Workscope',
        workscopeGoodwill: 'Goodwill Workscope',
        workscopeGoodwillStatus: 'Status of Goodwill Workscope',
        goodwillStatus: 'Goodwill Status',
        kindOfRoofWork: 'What kind of roof repair?',
        medicalNeeds: 'Special (Medical) Needs',
        homeownerAge: 'Homeowner Age',
        isSingleParent: 'Single Parent',
        numChildrenUnder18: 'Children Under 18',
        isFirstResponder: 'First Responder',
        isVeteran: 'Veteran',
        hasFloodInsurance: 'Flood Insurance',
        hasHomeInsurance: 'Home Insurance',
        femaStatus: 'FEMA Status',
        sbaStatus: 'SBA Status',
        residenceType: 'Residence Type',
        structureType: 'Structure Type',
        assessmentCompleted: 'Assessment Completed',
        search: 'Search',
        homeownerName: 'Homeowner Name',
        city: 'City',
        county: 'County',
        street: 'Street',
        updatedFrom: 'Updated (from)',
        updatedTo: 'Updated (to)',
        addedFrom: 'Added (from)',
        addedTo: 'Added (to)',
        activeStatus: 'Active',
    }
    return displayNames[key] || key
}

function HomeOwnersFilterIndicator() {
    const router = useRouter()
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const tabParam = searchParams.get('tab')
    const { workScopeOptions } = useWorkScopes()
    
    // Get current filter parameters (excluding pagination)
    const currentParams = useCurrentSearchParams<GetHomeOwnerQueryParamsType>([
        "overallStatus",
        "reliefStatus",
        "rebuildingStatus",
        "goodwillStatus",
        "workscope",
        "claimedBy",
        "workscopeStatus",
        "workscopeGoodwill",
        "workscopeGoodwillStatus",
        "roofing",
        "kindOfRoofWork",
        "medicalNeeds",
        "homeownerAge",
        "isSingleParent",
        "numChildrenUnder18",
        "isFirstResponder",
        "isVeteran",
        "hasFloodInsurance",
        "hasHomeInsurance",
        "femaStatus",
        "sbaStatus",
        "residenceType",
        "structureType",
        "assessmentCompleted",
        "search",
        "homeownerName",
        "city",
        "county",
        "street",
        "updatedFrom",
        "updatedTo",
        "addedFrom",
        "addedTo",
        "activeStatus",
    ])

    // Filter out empty values
    const activeFilters = Object.entries(currentParams).filter(([key, value]) => 
        value !== undefined && 
        value !== null && 
        value !== '' && 
        value !== 'undefined' &&
        !(Array.isArray(value) && value.length === 0) // Also filter out empty arrays
    )
    // Only show status chips when their parent scope is selected: show workscopeStatus only when workscope is set; show workscopeGoodwillStatus only when workscopeGoodwill is set
    const filteredActiveFilters = activeFilters.filter(([key]) => {
        if (key === 'workscopeGoodwillStatus') {
            const gw = currentParams.workscopeGoodwill
            return gw != null && (Array.isArray(gw) ? gw.length > 0 : true)
        }
        if (key === 'workscopeStatus') {
            const ws = currentParams.workscope
            return ws != null && (Array.isArray(ws) ? ws.length > 0 : true)
        }
        return true
    })

    if (filteredActiveFilters.length === 0) {
        return null
    }

    const removeFilter = (filterKey: string) => {
        const newParams = { ...currentParams } as Record<string, any>
        delete newParams[filterKey]
        
        const url = buildUrl(
            pathname,
            newParams,
            {
                previousParams: {
                    page: "1",
                    limit: "50",
                    ...(tabParam && { tab: tabParam })
                }
            }
        )
        router.push(url)
    }

    const clearAllFilters = () => {
        const url = buildUrl(
            pathname,
            {},
            {
                previousParams: {
                    page: "1",
                    limit: "50",
                    ...(tabParam && { tab: tabParam })
                }
            }
        )
        router.push(url)
    }

    return (
        <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Filter className="w-4 h-4" />
                <span>Active filters:</span>
            </div>
            
            {filteredActiveFilters.map(([key, value]) => (
                <div
                    key={key}
                    className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-sm"
                >
                    <span className="font-medium">{getFilterDisplayName(key)}:</span>
                    <span>{getFilterDisplayLabel(key, value, workScopeOptions)}</span>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-auto p-0 ml-1 hover:bg-primary/20"
                        onClick={() => removeFilter(key)}
                    >
                        <X className="w-3 h-3" />
                    </Button>
                </div>
            ))}
            
            <Button
                variant="default"
                size="sm"
                onClick={clearAllFilters}
                className="bg-primary hover:bg-primary/90 text-white font-bold whitespace-nowrap"
            >
                Clear all
            </Button>
        </div>
    )
}

export default HomeOwnersFilterIndicator 
